/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Form,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button as RSButton,
  Input as RSInput,
} from "reactstrap";
import { useMutation, useLazyQuery } from "@apollo/react-hooks";
import { getOr, get } from "lodash/fp";
import groupBy from "lodash/groupBy";
import { UPDATE_EVENT_COMPETITION } from "../../GraphQL/Mutations";
import {
  FETCH_COMPETITION_LIST,
  FETCH_COMPETITIONS_BY_EVENT,
} from "../../GraphQL/Queries";
import {
  PrimaryButton,
  LinkButton,
  FormTitle,
  InnerFormTitle,
  Hr,
} from "../../Components/Auth/Layout";
import useCurrentUser from "../../Hooks/userCurrentUser";
import useFormParams from "../../Hooks/useFormParams";
import { Checkbox } from "../../Components/Form";
import useHeaderComponentsMutation from "../../Hooks/useHeaderComponentsMutation";
import useGraphQLErrorExtractor from "../../Hooks/useGraphQLErrorExtractor";
import GenericAlert from "../../Components/GenericAlert";
import useUpdateEventCompetitionsCache from "../../Hooks/useUpdateEventCompetitionsCache";

const COMPETITION_STATES = {
  ACTIVE: 2,
  INACTIVE: 3,
};

const competitionTitleSort = (titleA, titleB) => {
  if (
    (titleA.startsWith("**") && titleB.startsWith("**")) ||
    (!titleA.startsWith("**") && !titleB.startsWith("**"))
  ) {
    // both pilots or neither pilot
    return titleA.localeCompare(titleB);
  }
  // reverse order so the ** ends up at the end
  return -1 * titleA.localeCompare(titleB);
};

const UpdateEventCompetitions = () => {
  const { currentUser } = useCurrentUser(true);
  const { id } = useParams();
  const [addedCompetitionsArray, setAddedCompetitionsArray] = useState([]);
  const [capacities, setCapacities] = useState({});
  const [initialCapacities, setInitialCapacities] = useState({});
  const [showCapacityModal, setShowCapacityModal] = useState(false);
  const [confirmInput, setConfirmInput] = useState("");
  const [reducedCompetitions, setReducedCompetitions] = useState([]);
  const updateCache = useUpdateEventCompetitionsCache(
    getOr(0, "tenantId", currentUser),
  );
  const extractError = useGraphQLErrorExtractor();
  const history = useHistory();
  useHeaderComponentsMutation({
    title: "Select Competitions",
    backLink: `/event-manage/${id}`,
  });
  const { params, onChange, setParams } = useFormParams({});

  const onCapacityChange = (competitionId, value) => {
    setCapacities((prev) => ({
      ...prev,
      [competitionId]: value === "" ? null : parseInt(value, 10),
    }));
  };

  const [updateEventCompetition, { loading, error }] = useMutation(
    UPDATE_EVENT_COMPETITION,
    {
      onCompleted: (data) => {
        updateCache(data.updateEventCompetition.eventCompetition);
        history.goBack();
      },
    },
  );

  const [fetchCompetitionList, { data: competitionData }] = useLazyQuery(
    FETCH_COMPETITION_LIST,
  );

  const competitions = getOr([], "listCompetitions", competitionData);

  const [fetchEventCompetitions] = useLazyQuery(FETCH_COMPETITIONS_BY_EVENT, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      setAddedCompetitionsArray(data.fetchCompetitionsByEvent);
      const caps = {};
      data.fetchCompetitionsByEvent.forEach((eventComp) => {
        if (eventComp.capacity != null) {
          caps[eventComp.competitionId] = eventComp.capacity;
        }
      });
      setCapacities(caps);
      setInitialCapacities(caps);
    },
  });

  useEffect(() => {
    if (currentUser) {
      fetchCompetitionList({
        variables: {
          tenantId: currentUser.tenantId,
        },
      });
    }
  }, [currentUser, fetchCompetitionList]);

  useEffect(() => {
    if (parseInt(id, 10)) {
      fetchEventCompetitions({
        variables: {
          eventId: parseInt(id, 10),
        },
      });
    }
  }, [id, fetchEventCompetitions]);

  const buildEventCompetitions = () => {
    // Build a map of all competition states from params
    const competitionStates = {};
    Object.entries(params).forEach(([competitionId, state]) => {
      competitionStates[parseInt(competitionId, 10)] = state;
    });

    // Merge params and capacities to include all competitions that have either been toggled or have capacity set
    const allCompetitionIds = new Set([
      ...Object.keys(params).map((id) => parseInt(id, 10)),
      ...Object.keys(capacities).map((id) => parseInt(id, 10)),
    ]);

    return Array.from(allCompetitionIds).map((competitionId) => {
      const state = competitionStates[competitionId];
      const isActive =
        state !== undefined ? state : checkEventCompetition(competitionId);
      const cap = capacities[competitionId] ?? null;
      const hadCapacity = initialCapacities[competitionId] != null;
      return {
        competitionId: String(competitionId),
        status: isActive
          ? COMPETITION_STATES.ACTIVE
          : COMPETITION_STATES.INACTIVE,
        capacity: cap,
        clearCapacity: hadCapacity && cap == null,
      };
    });
  };

  const executeSubmit = () => {
    const eventCompetitions = buildEventCompetitions();
    updateEventCompetition({
      variables: {
        tenantId: currentUser.tenantId,
        eventId: parseInt(id, 10),
        eventCompetitions,
      },
    });
  };

  const getCompetitionName = (competitionId) => {
    const comp = competitions.find(
      (c) => c.id === competitionId || c.id === String(competitionId),
    );
    return comp ? comp.title : `Competition ${competitionId}`;
  };

  const onSubmit = (e) => {
    e.preventDefault();

    const reduced = Object.entries(capacities)
      .filter(([compId, newCap]) => {
        const oldCap = initialCapacities[compId];
        return oldCap != null && newCap != null && newCap < oldCap;
      })
      .map(([compId, newCap]) => ({
        id: compId,
        name: getCompetitionName(compId),
        oldCapacity: initialCapacities[compId],
        newCapacity: newCap,
      }));

    if (reduced.length > 0) {
      setReducedCompetitions(reduced);
      setConfirmInput("");
      setShowCapacityModal(true);
    } else {
      executeSubmit();
    }
  };

  const getEventCompetitionObj = (competitionid) =>
    addedCompetitionsArray.filter(
      ({ competitionId }) => competitionId === parseInt(competitionid, 10),
    )[0];

  const checkEventCompetition = (competitionid) =>
    get("status", getEventCompetitionObj(competitionid)) === "active";

  const onCancel = () => {
    history.push(`/event-detail/${id}`);
  };

  const competitionsByCategory = groupBy(
    competitions,
    (counter) => counter.categoryId,
  );

  return (
    <>
      {error ? <GenericAlert>{extractError(error)}</GenericAlert> : null}
      <Form onSubmit={onSubmit}>
        <div className="column pt-3">
          <div className="pt-3" />
          <FormTitle color="#000">Select Competitions</FormTitle>
        </div>
        <div className="row py-5">
          <div className="col-12 mx-auto px-4">
            {Object.keys(competitionsByCategory).map((categoryId, index) => (
              <InnerFormTitle key={categoryId}>
                <p>
                  {get(
                    "title",
                    get("fetchCategory", competitionsByCategory[categoryId][0]),
                  )}
                </p>
                {Object.values(competitionsByCategory)
                  [index].sort((a, b) => competitionTitleSort(a.title, b.title))
                  .map((competition) => (
                    <div key={`${competition.id}`}>
                      <div
                        className="d-flex align-items-center"
                        style={{ gap: "1rem", paddingTop: "1rem" }}
                      >
                        <div style={{ flex: 1 }}>
                          <Checkbox
                            id={competition.id}
                            name={competition.id}
                            params={params}
                            onChange={onChange}
                            setParams={setParams}
                            text={competition.title}
                            subtext={`min: ${competition.minTeam}, max: ${competition.maxTeam}, ${competition.group ? "group comp" : "individual comp"}${competition.onlineSubmission ? ", requires online submission" : ""}`}
                            checked={checkEventCompetition(competition.id)}
                            hideHr
                          />
                        </div>
                        <div
                          className="d-flex align-items-center mb-3"
                          style={{ gap: "0.5rem" }}
                        >
                          <label
                            htmlFor={`capacity-${competition.id}`}
                            className="mt-2"
                            style={{ width: "70px", textAlign: "right" }}
                          >
                            {competition.group
                              ? "Team Capacity:"
                              : "Individual Capacity:"}
                          </label>
                          <input
                            type="number"
                            id={`capacity-${competition.id}`}
                            min="0"
                            placeholder="No limit"
                            value={capacities[competition.id] ?? ""}
                            onChange={(e) =>
                              onCapacityChange(competition.id, e.target.value)
                            }
                            className="form-control form-control-sm"
                            style={{ width: "90px", textAlign: "center" }}
                          />
                        </div>
                      </div>
                      <Hr />
                    </div>
                  ))}
              </InnerFormTitle>
            ))}
            <div className="w-100">
              <PrimaryButton
                loading={loading}
                htmltype="submit"
                disabled={loading}
              >
                {loading ? "Saving..." : "Save and Continue"}
              </PrimaryButton>
              <div className="pt-3">
                <LinkButton
                  className
                  onClick={() => onCancel()}
                  disabled={loading}
                >
                  Cancel
                </LinkButton>
              </div>
            </div>
          </div>
        </div>
      </Form>

      <Modal
        isOpen={showCapacityModal}
        toggle={() => setShowCapacityModal(false)}
      >
        <ModalHeader toggle={() => setShowCapacityModal(false)}>
          Confirm Capacity Reduction
        </ModalHeader>
        <ModalBody>
          <p>You are reducing the capacity for the following competitions:</p>
          <ul>
            {reducedCompetitions.map((comp) => (
              <li key={comp.id}>
                <strong>{comp.name}</strong>
                {`: ${comp.oldCapacity} → ${comp.newCapacity}`}
              </li>
            ))}
          </ul>
          <p className="mt-3">
            Type <strong>CONFIRM</strong> to proceed.
          </p>
          <RSInput
            value={confirmInput}
            onChange={(e) => setConfirmInput(e.target.value)}
            placeholder="Type CONFIRM"
          />
        </ModalBody>
        <ModalFooter>
          <RSButton
            color="danger"
            disabled={confirmInput !== "CONFIRM" || loading}
            onClick={() => {
              setShowCapacityModal(false);
              executeSubmit();
            }}
          >
            {loading ? "Saving..." : "Confirm Reduction"}
          </RSButton>
          <RSButton
            color="secondary"
            outline
            disabled={loading}
            onClick={() => setShowCapacityModal(false)}
          >
            Cancel
          </RSButton>
        </ModalFooter>
      </Modal>
    </>
  );
};
export default UpdateEventCompetitions;
